/**
 * PUT a local file to a presigned S3 URL.
 *
 * Used by the microVM guest's `BrowserCommandExecutor` to move a trace/video
 * artifact off the guest's filesystem without the bytes crossing the guest's
 * bandwidth-capped inbound endpoint — the worker mints the URL and hands it
 * in as a command argument (see `browser-client.interface.ts`'s
 * `ArtifactUploadTarget`), and the guest uploads out via its normal (uncapped)
 * egress path.
 *
 * Two S3 gotchas this must respect. Both were learned the expensive way in
 * `packages/runner-entrypoint`'s `renewAndUpload`, which #2893 deleted along
 * with the rest of the Fargate runner substrate — so this comment is now the
 * surviving record of them, not a pointer to one:
 *
 *  1. **Presigned PUTs reject chunked transfer-encoding.** An explicit
 *     `content-length` header is required.
 *  2. **A retried upload must re-create its body.** Re-issuing the same
 *     (already-drained) stream after a failed attempt "succeeds" with a
 *     zero-byte object — worse than failing, because the run looks like it
 *     uploaded. Each attempt below opens a fresh `ReadStream`.
 *
 * Upload failure is never swallowed — every attempt exhausted throws.
 *
 * @module local-browser/artifact-upload
 */
import { createReadStream } from 'node:fs';
import { stat } from 'node:fs/promises';

export interface UploadRetryPolicy {
  attempts: number;
  baseDelayMs: number;
}

const DEFAULT_RETRY: UploadRetryPolicy = { attempts: 3, baseDelayMs: 500 };

export interface PutFileToPresignedUrlDeps {
  /** Injectable for tests — avoids a real network call. */
  fetchImpl?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
  retry?: UploadRetryPolicy;
}

export class ArtifactUploadError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = 'ArtifactUploadError';
  }
}

/**
 * PUT `filePath`'s current contents to `url`. Returns the uploaded byte size
 * (read via `stat` immediately before each attempt, so a file that changed
 * between retries is measured freshly rather than trusting a stale count).
 *
 * Throws {@link ArtifactUploadError} if every attempt fails — callers must
 * treat that as a hard failure, not a degraded success.
 */
export async function putFileToPresignedUrl(
  filePath: string,
  url: string,
  deps: PutFileToPresignedUrlDeps = {}
): Promise<{ sizeBytes: number }> {
  const policy = deps.retry ?? DEFAULT_RETRY;
  const doFetch = deps.fetchImpl ?? fetch;
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));

  let lastError: unknown = null;
  for (let attempt = 1; attempt <= policy.attempts; attempt += 1) {
    try {
      // Fresh stat + fresh stream every attempt — never reuse a prior
      // attempt's (possibly drained) body.
      const stats = await stat(filePath);
      const body = createReadStream(filePath);
      const init: RequestInit & { duplex?: 'half' } = {
        method: 'PUT',
        body: body as unknown as BodyInit,
        // Required whenever the body is a stream; harmless otherwise.
        duplex: 'half',
        headers: { 'content-length': String(stats.size) },
      };
      const response = await doFetch(url, init);
      if (!response.ok) {
        throw new Error(`artifact upload failed with HTTP ${response.status}`);
      }
      return { sizeBytes: stats.size };
    } catch (cause) {
      lastError = cause;
      if (attempt < policy.attempts) {
        await sleep(policy.baseDelayMs * attempt);
      }
    }
  }
  throw new ArtifactUploadError(
    `Failed to upload ${filePath} to presigned URL after ${policy.attempts} attempt(s): ${
      lastError instanceof Error ? lastError.message : String(lastError)
    }`,
    lastError
  );
}
